import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import snakeCase from 'lodash/snakeCase';

import styled from 'styled-components';
import { ExclamationCircleOutlined } from '@ant-design/icons';

// Components
import Select from 'components/ui/SelectField';
import Banner from 'components/ui/Banner';
import { CONDITIONAL_ADDENDUM } from '../../CreateDocumentTemplateDialog';

// Helpers
import { iconColors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

const StyledSectionContainer = styled.div`
  margin-bottom: 16px;
  margin-top: 16px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledLabel = styled.label`
  margin-top: 10px;
`;

const StyledError = styled.div`
  color: red;
  margin-top: 5px;
`;

const LeaseItemType = connect((store) => ({
  conditionalMappings: store.landlordDocumentTemplates.availableConditionalTypeMappings,
  properties: store.landlord.properties.propertyMultiselect.properties,
}))(({ values, errors, touched, setFieldValue, conditionalMappings, properties }) => {
  const leaseItemTypes = [];
  let message = '';

  const [propertiesByLabel, setPropertiesByLabel] = useState([]);
  const [excludedProperties, setExcludedProperties] = useState([]);

  useEffect(() => {
    setPropertiesByLabel([]);
    values.has_warning = false;

    if (excludedProperties.length > 0) {
      // if list of properties is more than 1 which are not part of lease item type, then create the dynamic message for warning
      if (values.signable_doc_type === CONDITIONAL_ADDENDUM) {
        if (!values.has_warning) {
          values.has_warning = true;
        }

        // go through the list of excluded properties and print the property name
        properties.forEach((property) => {
          if (excludedProperties.includes(property.attributes['unique_id'])) {
            setPropertiesByLabel((arr) => [...arr, property.attributes['label']]);
          }
        });
      } else {
        // this scenario handles when user moves from conditional addendum document type to lease or fixed. In this case, we get rid of the warnings and reset the lease item type dropdown
        values.has_warning = false;
        values.conditional_addendum_name = undefined;

        setExcludedProperties([]);
      }

      return;
    }

    if (excludedProperties.length === 0 && values.signable_doc_type !== CONDITIONAL_ADDENDUM) {
      values.conditional_addendum_name = undefined;
      return;
    }
  }, [values, excludedProperties, properties]);

  if (conditionalMappings && conditionalMappings.length > 0) {
    conditionalMappings.forEach((mapping) => {
      const mappingData = {
        value: snakeCase(mapping.name),
        label: mapping.name,
        ids: mapping.property_unique_ids,
      };

      leaseItemTypes.push(mappingData);
    });
  }

  const handleChange = (value) => {
    setExcludedProperties([]);

    const leaseItemType = leaseItemTypes.filter((element) => element.value === value);

    //loop through the selected properties and see if selected lease item type has that property
    const leaseItemTypeProperties = leaseItemType[0].ids;

    values.conditional_addendum_name = leaseItemType[0].label;

    values.property_uuids.forEach((property_id) => {
      if (!leaseItemTypeProperties.includes(property_id)) {
        setExcludedProperties((arr) => [...arr, property_id]);
      }
    });
  };

  propertiesByLabel.forEach((label, index) => {
    if (index !== propertiesByLabel.length - 1) {
      message = message + `${label}, `;
    } else {
      message = message + `${label} `;
    }
  });

  if (values.signable_doc_type === CONDITIONAL_ADDENDUM) {
    return (
      <React.Fragment>
        <StyledLabel>
          {getTranslatedString('landlordPortal.documentTemplate.selectLeaseItemType')}
        </StyledLabel>
        <StyledSelect
          name="conditional_addendum_name"
          showSearch
          placeholder={getTranslatedString('landlordPortal.documentTemplate.selectLeaseItemType')}
          size="large"
          value={snakeCase(values.conditional_addendum_name)}
          onChange={(value) => {
            handleChange(value);
            const leaseItemType = leaseItemTypes.find((element) => element.value === value);
            setFieldValue('conditional_addendum_name', leaseItemType.label);
          }}
        >
          {leaseItemTypes.map((leaseItemType) => (
            <Select.Option value={leaseItemType.value} key={`${leaseItemType.value}`}>
              {leaseItemType.label}
            </Select.Option>
          ))}
        </StyledSelect>

        {excludedProperties.length > 0 && (
          <StyledSectionContainer>
            <Banner
              message="Warning"
              description={`${getTranslatedString(
                'landlordPortal.documentTemplate.leaseDocumentSelectionWarning'
              )}${' '}${message}`}
              showIcon
              icon={
                <ExclamationCircleOutlined
                  style={{ color: iconColors.EXCLAMATION_CIRCLE_FILLED }}
                />
              }
            />
          </StyledSectionContainer>
        )}

        {errors.conditional_addendum_name && touched.conditional_addendum_name ? (
          <StyledError>{errors.conditional_addendum_name}</StyledError>
        ) : null}
      </React.Fragment>
    );
  } else {
    return <></>;
  }
});

export default LeaseItemType;
